import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef, useState, useEffect } from 'react';
import { useTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { Tab } from './Tab.js';
import { Container, TabsContainer, StyledCell, ChevronContainer, FlyoutCellContainer } from './Tabs.styles.js';
import { Flyout } from '../Flyout/Flyout.js';
import ButtonIcon from '../Inputs/Button/ButtonIcon/ButtonIcon.js';

var BUTTON_WIDTH = 32;
var Tabs = function Tabs(_ref) {
  var _ref$tabs = _ref.tabs,
    tabs = _ref$tabs === void 0 ? [] : _ref$tabs,
    onTabChange = _ref.onTabChange,
    _ref$isParent = _ref.isParent,
    isParent = _ref$isParent === void 0 ? false : _ref$isParent,
    _ref$activeTab = _ref.activeTab,
    activeTab = _ref$activeTab === void 0 ? undefined : _ref$activeTab,
    _ref$moreButtonLabel = _ref.moreButtonLabel,
    moreButtonLabel = _ref$moreButtonLabel === void 0 ? 'More' : _ref$moreButtonLabel,
    _ref$testId = _ref.testId,
    testId = _ref$testId === void 0 ? undefined : _ref$testId;
  var theme = useTheme();
  var isMobile = theme.mqMatch('sm');
  var containerRef = useRef(null);
  var relativeTriggerRef = useRef(null);
  var container = containerRef.current;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isMoreOpen = _useState2[0],
    setIsMoreOpen = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    isMaxLeft = _useState4[0],
    setIsMaxLeft = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    isMaxRight = _useState6[0],
    setIsMaxRight = _useState6[1];
  var _useState7 = useState(0),
    _useState8 = _slicedToArray(_useState7, 2),
    tabsContainerScrollWidth = _useState8[0],
    setTabsContainerScrollWidth = _useState8[1];
  var _useState9 = useState(0),
    _useState10 = _slicedToArray(_useState9, 2),
    tabsContainerWidth = _useState10[0],
    setTabsContainerWidth = _useState10[1];
  var _useState11 = useState(tabs.length),
    _useState12 = _slicedToArray(_useState11, 2),
    tabsToFit = _useState12[0],
    setTabsToFit = _useState12[1];
  var isScrollable = tabsContainerScrollWidth > tabsContainerWidth;
  var isScrollMobile = isScrollable && isMobile;
  var isMoreButtonVisible = isMobile && (tabs === null || tabs === void 0 ? void 0 : tabs.length) > 0 && tabsToFit < tabs.length;
  useEffect(function () {
    var handleResize = function handleResize() {
      setTabsContainerWidth(container === null || container === void 0 ? void 0 : container.clientWidth);
      setTabsContainerScrollWidth(container === null || container === void 0 ? void 0 : container.scrollWidth);
      if ((container === null || container === void 0 ? void 0 : container.scrollWidth) > (container === null || container === void 0 ? void 0 : container.clientWidth) && isMobile && tabsToFit > -1) {
        setTabsToFit(tabsToFit - 1);
      }
      if (!isMobile) {
        setTabsToFit(tabs.length);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return function () {
      window.removeEventListener('resize', handleResize);
    };
  }, [container, tabsToFit, isMobile, tabs, isMoreButtonVisible]);
  var handleScroll = function handleScroll(e) {
    setIsMaxLeft(e.target.scrollLeft === 0);
    var maxRight = e.target.scrollWidth - e.target.scrollLeft <= e.target.clientWidth;
    setIsMaxRight(maxRight);
  };
  var scrollRight = function scrollRight() {
    var allTabs = Array.from(container.children);
    var firstHiddenTab = allTabs.find(function (tab) {
      var rightEdge = tab.offsetLeft + tab.offsetWidth;
      return rightEdge > container.scrollLeft + container.clientWidth;
    });
    if (firstHiddenTab) {
      var scrollBy = firstHiddenTab.offsetLeft - container.scrollLeft;
      container.scrollLeft += scrollBy - BUTTON_WIDTH;
    }
  };
  var scrollLeft = function scrollLeft() {
    var allTabs = Array.from(container.children).reverse();
    var firstHiddenTab = allTabs.find(function (tab) {
      return tab.offsetLeft < container.scrollLeft;
    });
    if (firstHiddenTab) {
      container.scrollLeft = firstHiddenTab.offsetLeft - tabsContainerWidth + firstHiddenTab.clientWidth + BUTTON_WIDTH;
    }
  };
  return React__default.createElement(Container, {
    $hidden: isScrollMobile,
    "data-testid": testId
  }, React__default.createElement(TabsContainer, {
    ref: containerRef,
    onScroll: handleScroll,
    role: "tablist"
  }, (tabs === null || tabs === void 0 ? void 0 : tabs.length) > 0 && tabs.slice(0, tabsToFit).map(function (tab) {
    return React__default.createElement(Tab, {
      key: tab.value,
      value: tab.value,
      label: tab.label,
      isDisabled: tab.isDisabled,
      icon: tab.icon,
      displayText: tab.displayText,
      isActive: tab.value === activeTab,
      isParentTab: isParent,
      onTabChange: onTabChange,
      testId: testId
    });
  }), isMoreButtonVisible && React__default.createElement(StyledCell, {
    ref: relativeTriggerRef
  }, React__default.createElement(ButtonIcon, {
    onClick: function onClick() {
      return setIsMoreOpen(!isMoreOpen);
    },
    variant: "ghost",
    icon: "".concat(isMoreOpen ? 'ChevronUp' : 'ChevronDown'),
    displayText: "left",
    size: "small"
  }, moreButtonLabel))), isScrollable && !isMaxLeft && React__default.createElement(ChevronContainer, {
    $isRightAligned: false
  }, React__default.createElement(ButtonIcon, {
    variant: "secondary",
    icon: "ChevronLeft",
    onClick: scrollLeft,
    ariaLabel: "scroll-left"
  })), isScrollable && !isMaxRight && React__default.createElement(ChevronContainer, {
    $isRightAligned: true
  }, React__default.createElement(ButtonIcon, {
    variant: "secondary",
    icon: "ChevronRight",
    onClick: scrollRight,
    ariaLabel: "scroll-right"
  })), isMoreOpen && (relativeTriggerRef === null || relativeTriggerRef === void 0 ? void 0 : relativeTriggerRef.current) && React__default.createElement(Flyout, {
    onOutsideClick: function onOutsideClick() {
      return setIsMoreOpen(false);
    },
    triggerPosition: relativeTriggerRef.current.getBoundingClientRect(),
    role: "tablist"
  }, tabs.slice(tabsToFit).map(function (_ref2) {
    var value = _ref2.value,
      label = _ref2.label,
      isDisabled = _ref2.isDisabled,
      icon = _ref2.icon,
      displayText = _ref2.displayText;
    return React__default.createElement(FlyoutCellContainer, {
      key: value
    }, React__default.createElement(Tab, {
      value: value,
      label: label,
      isDisabled: isDisabled,
      icon: icon,
      displayText: displayText,
      isActive: value === activeTab,
      onTabChange: onTabChange
    }));
  })));
};
Tabs.propTypes = {
  activeTab: PropTypes.string,
  isParent: PropTypes.bool,
  moreButtonLabel: PropTypes.string,
  onTabChange: PropTypes.func.isRequired,
  tabs: PropTypes.arrayOf(PropTypes.shape({
    displayText: PropTypes.string,
    icon: PropTypes.string,
    isDisabled: PropTypes.bool,
    label: PropTypes.string.isRequired,
    testId: PropTypes.string,
    value: PropTypes.string.isRequired
  })).isRequired,
  testId: PropTypes.string
};

export { Tabs, Tabs as default };
//# sourceMappingURL=Tabs.js.map
